import React, { useState } from 'react';
import { Create, useForm } from '@refinedev/antd';
import { useNavigation } from '@refinedev/core';
import {
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Collapse,
  Upload,
  Button,
  Image,
  message,
  Divider,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import { EquipmentCategory, EquipmentDifficultyLevel } from '@wellverse/shared-ts';
import { API_BASE_URL } from '../../providers/dataProvider';
import { EquipmentLanguageTabs, normalizeEquipmentTranslations } from './LanguageTabs';


export const EquipmentCatalogCreate: React.FC = () => {
  const { formProps, onFinish } = useForm({ resource: 'equipment-catalog' });
  const { push } = useNavigation();

  // Staged image files (not yet uploaded — need entry ID first)
  const [stagedFiles, setStagedFiles] = useState<{ file: File; preview: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleAddFiles = (files: FileList | File[]) => {
    const newEntries = Array.from(files)
      .filter((f) => f.type.startsWith('image/'))
      .map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
    setStagedFiles((prev) => [...prev, ...newEntries]);
  };

  const removeStaged = (index: number) => {
    setStagedFiles((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadImages = async (entryId: string) => {
    const authToken = localStorage.getItem('access_token');
    for (const { file } of stagedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(`${API_BASE_URL}/equipment-catalog/${entryId}/images`, formData, {
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            'Content-Type': 'multipart/form-data',
          },
        });
      } catch (err: any) {
        console.warn(`Failed to upload image for ${entryId}:`, err.message);
      }
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const result = await onFinish({
        ...values,
        translations: normalizeEquipmentTranslations(values.translations),
      });
      const entryId = String(result?.data?.id);
      if (entryId && stagedFiles.length > 0) {
        message.loading({ content: `Uploading ${stagedFiles.length} image(s)…`, key: 'upload', duration: 0 });
        await uploadImages(entryId);
        message.success({ content: 'Images uploaded', key: 'upload' });
      }
      message.success('Equipment created');
      push('/equipment-catalog');
    } catch (err: any) {
      message.error(err?.message || 'Failed to create equipment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Create
      resource="equipment-catalog"
      saveButtonProps={{ loading: submitting, onClick: () => formProps.form?.submit() }}
    >
      <Form {...formProps} layout="vertical" onFinish={handleSubmit}>
        {/* Reference Images Upload */}
        <Form.Item label="Reference Images">
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
              {stagedFiles.map((entry, i) => (
                <div
                  key={entry.preview}
                  style={{
                    position: 'relative',
                    width: 120,
                    height: 120,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid #e8e8e8',
                  }}
                >
                  <Image
                    src={entry.preview}
                    alt={`Preview ${i}`}
                    width={120}
                    height={120}
                    style={{ objectFit: 'cover' }}
                    preview={{ mask: 'View' }}
                  />
                  <Button
                    type="text"
                    danger
                    size="small"
                    onClick={() => removeStaged(i)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      background: 'rgba(255,255,255,0.85)',
                      borderRadius: 4,
                    }}
                  >
                    X
                  </Button>
                </div>
              ))}
              <Upload
                beforeUpload={(file: any) => {
                  handleAddFiles([file]);
                  return false; // prevent auto upload
                }}
                showUploadList={false}
                accept="image/*"
                multiple
              >
                <div
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: 8,
                    border: '1px dashed #434343',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#898989',
                  }}
                >
                  <PlusOutlined style={{ fontSize: 24 }} />
                  <span style={{ fontSize: 12, marginTop: 4 }}>Upload</span>
                </div>
              </Upload>
            </div>
            {stagedFiles.length > 0 && (
              <div style={{ color: '#898989', fontSize: 12 }}>
                {stagedFiles.length} image(s) selected. They will be uploaded after creation.
              </div>
            )}
          </div>
        </Form.Item>

        <EquipmentLanguageTabs />

        <Divider orientation="left" plain>
          Shared
        </Divider>

        <Form.Item label="Brand" name="brand" rules={[{ required: true }]}>
          <Input placeholder="e.g. Matrix Fitness" />
        </Form.Item>
        <Form.Item label="Model" name="model" rules={[{ required: true }]}>
          <Input placeholder="e.g. G3-S80" />
        </Form.Item>
        <Form.Item label="Category" name="category" rules={[{ required: true }]}>
          <Select placeholder="Select category">
            {Object.values(EquipmentCategory).map((c) => (
              <Select.Option key={c} value={c}>{c}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Difficulty Level" name="difficultyLevel" initialValue={EquipmentDifficultyLevel.BEGINNER}>
          <Select>
            {Object.values(EquipmentDifficultyLevel).map((l) => (
              <Select.Option key={l} value={l}>{l}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Target Muscle Groups" name="targetMuscleGroups">
          <Select mode="tags" placeholder="e.g. chest, triceps, shoulders" />
        </Form.Item>
        <Form.Item label="Tags" name="tags">
          <Select mode="tags" placeholder="e.g. compound, upper-body" />
        </Form.Item>
        <Form.Item
          label="Aliases"
          name="aliases"
          extra="Alternate names used when matching scans (e.g. Chest Push → Chest Press)"
        >
          <Select mode="tags" placeholder="e.g. Chest Push, 推胸" />
        </Form.Item>
        <Form.Item label="Video Links" name="videoLinks">
          <Select mode="tags" placeholder="Paste video URLs and press Enter" />
        </Form.Item>
        <Form.Item label="Image URLs" name="imageUrls">
          <Select mode="tags" placeholder="Paste image URLs and press Enter" />
        </Form.Item>

        <Collapse
          style={{ marginBottom: 24 }}
          items={[
            {
              key: 'workout',
              label: 'Suggested Workout',
              children: (
                <>
                  <Form.Item label="Sets" name={['suggestedWorkout', 'sets']} initialValue={3}>
                    <InputNumber style={{ width: '100%' }} min={1} />
                  </Form.Item>
                  <Form.Item label="Reps" name={['suggestedWorkout', 'reps']}>
                    <Input placeholder="e.g. 8-12" />
                  </Form.Item>
                  <Form.Item label="Recommended Weight" name={['suggestedWorkout', 'recommendedWeight']}>
                    <Input placeholder="e.g. 15-20kg" />
                  </Form.Item>
                  <Form.Item label="Rest Between Sets" name={['suggestedWorkout', 'restBetweenSets']}>
                    <Input placeholder="e.g. 60-90 seconds" />
                  </Form.Item>
                  <Form.Item label="Frequency" name={['suggestedWorkout', 'frequency']}>
                    <Input placeholder="e.g. 2-3 times/week" />
                  </Form.Item>
                  <Form.Item label="Variations" name={['suggestedWorkout', 'variations']}>
                    <Select mode="tags" placeholder="e.g. Close grip, Wide grip" />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'formGuide',
              label: 'Form Guide',
              children: (
                <>
                  <Form.Item label="Setup" name={['formGuide', 'setup']}>
                    <Input.TextArea rows={2} placeholder="How to set up for this exercise" />
                  </Form.Item>
                  <Form.Item label="Execution" name={['formGuide', 'execution']}>
                    <Input.TextArea rows={3} placeholder="Step-by-step execution instructions" />
                  </Form.Item>
                  <Form.Item label="Common Mistakes" name={['formGuide', 'commonMistakes']}>
                    <Select mode="tags" placeholder="e.g. Arching back, Locking elbows" />
                  </Form.Item>
                  <Form.Item label="Tips" name={['formGuide', 'tips']}>
                    <Select mode="tags" placeholder="e.g. Breathe out on exertion" />
                  </Form.Item>
                </>
              ),
            },
          ]}
        />

        <Form.Item label="Active" name="isActive" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
      </Form>
    </Create>
  );
};
